import React, { useCallback, useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  applySavedViewToSearchParams,
  canonicalizeSavedViewSearchParams,
  listSavedViewsForTab,
  resolveSavedView,
  savedViewParam,
  type SavedViewId,
  type SavedViewTab,
} from '../../domain/safetyCases/workspaceSavedViews';

/**
 * Compact URL-persisted saved-view chips for Attention / registers.
 * Owns `view` (+ aligned `tab` / `attention`) without portfolio wiring.
 * Unsupported chips for the active tab are omitted (not greyed).
 * No selection = All.
 */
export function WorkspaceSavedViewsBar({ tab }: { tab?: SavedViewTab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = String(searchParams.get(savedViewParam()) || '').trim();
  const activeView = resolveSavedView(rawView);
  const activeTab = tab || (String(searchParams.get('tab') || 'attention') as SavedViewTab);
  const views = listSavedViewsForTab(
    activeTab === 'attention' || activeTab === 'cases' || activeTab === 'scans' || activeTab === 'records'
      ? activeTab
      : 'attention',
  );

  useEffect(() => {
    // Keep recognized saved-view URLs canonical; unknown view ids do not own manual filters.
    if (!rawView) return;
    const next = new URLSearchParams(searchParams);
    if (canonicalizeSavedViewSearchParams(next, activeTab)) {
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, rawView, searchParams, setSearchParams]);

  const selectView = useCallback((viewId: SavedViewId) => {
    const next = new URLSearchParams(searchParams);
    if (activeView === viewId) {
      applySavedViewToSearchParams(next, null);
    } else {
      applySavedViewToSearchParams(next, viewId, activeTab);
    }
    setSearchParams(next, { replace: true });
  }, [activeTab, activeView, searchParams, setSearchParams]);

  const compactChrome = true;

  return (
    <Stack
      spacing={compactChrome ? 0.5 : 0.75}
      data-testid="workspace-saved-views"
      sx={{
        border: compactChrome ? 0 : 1,
        borderColor: 'divider',
        borderRadius: 2,
        px: compactChrome ? 0 : 1.5,
        py: compactChrome ? 0.25 : 1.25,
      }}
    >
      <Box>
        <Typography variant={compactChrome ? 'body2' : 'subtitle2'} sx={{ fontWeight: 700 }}>
          Saved views
        </Typography>
        {!compactChrome ? (
        <Typography variant="caption" color="text.secondary">
          Single-select filters for this tab. No selection means All. No bulk actions.
        </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} data-testid="workspace-saved-views-chips">
        {views.map((view) => {
          const selected = activeView === view.id;
          return (
            <Button
              key={view.id}
              size="small"
              variant={selected ? 'contained' : 'outlined'}
              aria-pressed={selected}
              data-testid={`saved-view-${view.id}`}
              onClick={() => selectView(view.id)}
              sx={{ textTransform: 'none', borderRadius: 999, px: 1.25, minHeight: 30 }}
            >
              {view.label}
            </Button>
          );
        })}
      </Box>
    </Stack>
  );
}
